import { ref } from "valtio";

import type { AppState, DigAppState } from "@/__main__/app-state.mjs";
import {
  __ONLY_WRITE_STATE_FROM_ACTIONS as writeState,
  addRecentlyPersistedPath,
  readState,
} from "@/__main__/app-state.mjs";
import {
  IS_DEV,
  IS_NODE,
  isInitial,
  isPersistent,
  isPopulatedFromDB,
  isVolatile,
  PATH_DELIMITER,
} from "@/__main__/constants.mjs";
import type {
  ModelDefinition,
  TransformModelFn,
} from "@/__main__/data-model.mjs";
import {
  __INTERNAL_ROOT_MODEL_SYMBOL as rootModel,
  __INTERNAL_VALIDATION_SYMBOL as isValidated,
  dataModelKey,
  withAccessValidation,
} from "@/__main__/data-model.mjs";
import dbObj, {
  __PRIVATE_DB_SYMBOL_FOR_INTERNAL_USE_NOT_APPLICATION_CODE,
} from "@/__main__/db.mjs";
import mainRefs from "@/__main__/refs.mjs";
import appEventBus from "@/app/app-event-bus.mjs";
import checkGetData from "@/util/check-get-data.mjs";
import clientOnline from "@/util/client-online.mjs";
import {
  GraphQLError,
  NetworkError,
  ValidationFailedError,
} from "@/util/custom-errors.mjs";
import deepEqual from "@/util/deep-equal.mjs";
import deepRef from "@/util/deep-ref.mjs";
import { devDebug, devError } from "@/util/dev.mjs";
import digPath from "@/util/dig-path.mjs";
import globals from "@/util/global-whitelist.mjs";
import isObject from "@/util/is-object.mjs";
import optionalMerge from "@/util/optional-merge.mjs";
import retry from "@/util/retry-promise.mjs";
import writeKeyPath from "@/util/state-cleanup.mjs";
import symbolName from "@/util/symbol-name.mjs";

export const EVENT_NETWORK_ERROR = "EVENT_NETWORK_ERROR";

if (!IS_NODE) {
  mainRefs.ERROR_BACKOFF_TIME = 30 * 1000;
  mainRefs.DEFAULT_BACKOFF_TIME = 5 * 60 * 1000;
}

const dbRef = {
  db: dbObj[__PRIVATE_DB_SYMBOL_FOR_INTERNAL_USE_NOT_APPLICATION_CODE],
};

// This is meant to be an entry point for hooking/testing.
// Do not copypaste this unless there is a need for hooking into or testing
// a particular fetch. Instead, just use fetch directly if there is a need for it.
export const fetchRef: {
  fetch?: typeof globals.fetch;
  controller?: AbortController | { signal: AbortSignal };
} = {};

export const isLoading: unique symbol = symbolName("post-is-loading");

// This is giga hacky but is needed because globals.fetch may not be ready yet.
queueMicrotask(() => {
  fetchRef.fetch = globals.fetch;
});

function associateValueToModel<V>(data: V, model: ModelDefinition): V {
  if (!IS_DEV) {
    return data;
  }
  data[dataModelKey] = model;
  return withAccessValidation(data, model);
}

async function fetchJSON(
  fetch: typeof globals.fetch,
  url: string,
  {
    httpMethod = "GET",
    body,
    headers,
    shouldRetry = true,
    acceptPartialErrors = true,
  }: {
    httpMethod?: string;
    body?: BodyInit;
    headers?: HeadersInit;
    shouldRetry?: boolean;
    acceptPartialErrors?: boolean;
  },
) {
  // Sanitize empty headers.
  for (const key in headers) {
    if (!headers[key]) delete headers[key];
  }

  const res = await retry(
    () =>
      clientOnline().then(() =>
        // eslint-disable-next-line blitz/prefer-get-data
        fetch(url, {
          method: httpMethod,
          body,
          headers,
          signal: fetchRef.controller?.signal,
        }),
      ),
    { maxRetries: shouldRetry && httpMethod === "GET" ? 1 : 0 },
  );

  if (!res.ok) {
    appEventBus.emit(EVENT_NETWORK_ERROR, url);
    const text = await res.text();
    const hostName = url.match(/:\/\/(.+?)\//i)?.[1];
    const pathName = url.match(/(?<!:\/?)(\/.+?)(?:\?|$)/i)?.[1];
    const gqlQuery = url.match(/query\+(.*?)%(?:7B|28)/i)?.[1] ?? "";
    const error = new NetworkError(
      `Server error. Host: "${hostName}". Path: "${pathName}"${
        gqlQuery ? `. GQL query: ${gqlQuery}` : ""
      }`,
    );
    const { status: statusCode } = res;
    Object.assign(error, { text, url, statusCode });
    throw error;
  }

  const json = res.status === 204 ? null : await res.json();

  // GraphQL is insane
  // Let's figure out if we should legit throw an error or not.
  const keys = Object.keys(json?.data ?? {});
  const hasData = json?.data?.[keys[0]];

  for (const jsonError of json?.errors ?? []) {
    const errorMessage: string | undefined =
      typeof jsonError?.message === "object"
        ? JSON.stringify(jsonError.message)
        : jsonError?.message;

    // if (!IS_DEV && mainRefs.checkForAuthorization(errorMessage, url)) return;

    const queryName = url.match(/query\+(.+?)%/i)?.[1];
    const hostName = url.match(/:\/\/(.+?)\//i)?.[1];
    const error = new GraphQLError(
      `GraphQL - "${errorMessage}". Query: "${queryName}". Host: "${hostName}"`,
      {
        url: url,
        errorMessage: errorMessage,

        statusCode: jsonError?.details?.http_code,
        errorCode: jsonError?.code,
      },
    );

    if (!hasData || !acceptPartialErrors) {
      appEventBus.emit(EVENT_NETWORK_ERROR, url);
      throw error;
    }
    devDebug(
      "This error is safe to ignore, and only indicates a partial error while retrieving data.",
      error,
    );
  }

  return json;
}

export const readPath = (path, state) => path.reduce((c, s) => c?.[s], state);

async function populatePathFromDB<D>(
  path: readonly PropertyKey[],
  initialValue: D | null,
  root: object,
): Promise<D | null> {
  const [record] = (await dbRef.db.find([path.join(PATH_DELIMITER)])) as [
    D | null,
  ];

  if (!record) return null;

  let curr = root;

  const keys = path.slice();
  const lastKey = keys.pop();

  for (const key of keys) {
    // if using readStack, it requires additional safety
    // if (!curr.hasOwnProperty(key)) curr[key] = {};
    curr = curr[key];
  }

  let value = record;

  if (isObject(value)) {
    // Assign keys from the initial value to what we got from the DB.
    if (initialValue && isObject(initialValue)) {
      optionalMerge(value, initialValue);
    }

    // https://github.com/pmndrs/valtio#holding-objects-in-state-without-tracking-them
    value = deepRef(value);
    value[isPopulatedFromDB] = true;
  }

  curr[lastKey] = value;

  return value;
}

type ReadDataOptions = {
  root?: object;

  forceReadFromDB?: boolean;
  initializeOnce?: boolean;
};

// With a different `root`, dig out the value
export async function readData<
  const P extends readonly PropertyKey[],
  Root extends object,
>(
  writeStatePath: P,
  options: ReadDataOptions & { root: object },
): Promise<DigValue<P, Root>>;

export async function readData<const P extends readonly PropertyKey[]>(
  writeStatePath: P,
  options?: ReadDataOptions & { root?: never },
): Promise<DigAppState<P>>;

/**
 * Hydrate state from the database.
 */
export async function readData<const P extends readonly PropertyKey[]>(
  writeStatePath: P,
  { root, ...options }: ReadDataOptions = {},
): Promise<unknown> {
  // Backwards compatibility
  root = writeStatePath?.["root"] ?? root;

  let currentValue = readFromMemory(writeStatePath, {
    root: root,
  });

  if (options.initializeOnce && currentValue && !currentValue[isInitial])
    return currentValue;

  if (
    currentValue === undefined ||
    currentValue?.[isInitial] ||
    // force read from DB if option is set
    options.forceReadFromDB
  ) {
    // if currentValue[isInitial], currentValue is initialState value.
    // so the following call is merging initialState with DB.
    currentValue = await populatePathFromDB(
      writeStatePath,
      currentValue,
      root ?? writeState,
    );

    const [r, lKey] = digPath(writeStatePath, root ?? readState);

    // Signal that we're about to write.
    mainRefs.allowWrite();

    // only write if there was not null found from DB except when force is set.
    if (currentValue !== null || options.forceReadFromDB) {
      r[lKey] = currentValue;
    }
    // remove the isInitial tag from the default object if null was found from the DB
    // and initializeOnce is set
    if (currentValue === null && options.initializeOnce) {
      delete r[lKey]?.[isInitial];
    }
  }

  if (currentValue !== undefined && !root) {
    addRecentlyPersistedPath(writeStatePath);
    writeKeyPath(writeStatePath);
  }

  return currentValue;
}

const recentURLs = new Set<DataSource>();
mainRefs.recentURLs = recentURLs;

function shouldReturnCachedValue<D>(
  currentValue: unknown,
  dataSource: DataSource,
  { shouldFetchIfPathExists }: SharedFetchOptions<D>,
) {
  return Boolean(
    currentValue &&
      !currentValue[isInitial] &&
      (!shouldFetchIfPathExists || recentURLs.has(dataSource)),
  );
}

// Extracting this into a separate function makes the closure containing the
// setTimeout function retain less memory.
function trackURL(dataSource: DataSource, networkBackOffTime: number) {
  if (!networkBackOffTime || IS_NODE) return;

  recentURLs.add(dataSource);

  if (IS_DEV) {
    const maxTimeout = Math.pow(2, 31); // 32-bit signed int
    if (networkBackOffTime >= maxTimeout) {
      devError(
        `Network back-off time is set to ${networkBackOffTime}, which is equal to ${(
          networkBackOffTime /
          (1000 * 60 * 60 * 24 * 365)
        ).toFixed(2)} years. This is most definitely a mistake.`,
        dataSource,
      );
    }
  }

  setTimeout(() => {
    recentURLs.delete(dataSource);
  }, networkBackOffTime);
}

export function readFromMemory(
  writeStatePath: readonly PropertyKey[],
  { root, shouldThrow }: { root?: object; shouldThrow?: boolean } = {},
): unknown {
  if (!writeStatePath) return undefined;

  // Backwards compatibility
  root = writeStatePath?.["root"] ?? root;

  const [r, lKey] = digPath(writeStatePath, root ?? readState);
  const value = r && r[lKey];

  if (value instanceof Error && shouldThrow !== false) throw value;

  return value;
}

// Keeping track to prevent concurrent requests for the same URL!
const inFlightURLPromiseMap = new Map();

/**
 * Generally speaking the defaults should handle most use cases, and it is
 * advisable not to change these unless necessary. However, a few common ones
 * to customize:
 *
 * - `shouldFetchIfPathExists`: turn this off for immutable data
 * - `mergeFn`: use this for safely merging data if needed, such as matchlist
 * - `httpMethod`: self-explanatory
 * - `headers`: self-explanatory
 */
type SharedFetchOptions<D> = {
  /**
   * This is used to set a different root for the state, by default it is just
   * `writeState`, which is what you want 99.9% of the time.
   *
   * Default: `writeState`
   */
  root?: object;

  /**
   * By default, it will read the database for a result and write that to
   * the app state, and then do any network request in the background.
   * The main reason to turn this off is if showing the cached state before
   * the network request finished is not desirable.
   *
   * Default: `false`
   */
  shouldSkipDBRead?: boolean;

  /**
   * Unless the data is immutable such as static data, this should be `true`.
   *
   * Default: `true`
   */
  shouldFetchIfPathExists?: boolean;

  /**
   * Network back-off time lets us call the same API endpoint as many times as
   * we want safely without spamming requests. This does not need customization,
   * unless the data is known to change faster than the default back-off time.
   *
   * Default: `5 * 60 * 1000` (5 minutes)
   */
  networkBackOffTime?: number;

  /**
   * Generally speaking there is no need to customize the expiry time, because
   * the default is assumed to work for most use cases. When this is set, this
   * ensures that the cached data will expire past the specified timestamp.
   *
   * Default: `Date.now() + 7 * 24 * 60 * 60 * 1000` (1 week from now)
   */
  expiryTime?: number;

  /**
   * This is only ever used for hooking into or testing, meant for private use.
   *
   * Default: `globalThis.fetch`
   */
  fetch?: typeof fetchRef.fetch;

  /**
   * The merge function is used when there is new data that needs to overwrite
   * old data, but not all of it. For example, this can be used to merge two
   * matchlists together. If one matchlist has matches that are not in the other
   * matchlist, then this can be used to return a union of all matches.
   *
   * Default: `null`
   */
  mergeFn?: (current: D, next: D) => D;

  /**
   * Use this if you need to customize HTTP request method.
   *
   * Default: `get`
   */
  httpMethod?: string;

  /**
   * Use this if you need to customize HTTP request headers.
   *
   * Default: `{}`
   */
  headers?: HeadersInit;

  /**
   * Set this to false if you want to fail the request if there is at least one error in the GraphQL response
   *
   * Default: true
   */
  acceptPartialErrors?: boolean;
};

type FetchDataOptions<D> = SharedFetchOptions<D> & {
  body?: BodyInit;
};

export type DataSource<D = unknown> = PromiseValue<D> | (() => D) | D | string;

type ResolveDataSource<D> =
  D extends Promise<infer I>
    ? I
    : D extends () => infer I
      ? I
      : D extends string
        ? unknown
        : D;

type DigAppStateOr<
  WriteStatePath extends readonly PropertyKey[] | undefined,
  Fallback,
> = WriteStatePath extends undefined ? Fallback : DigAppState<WriteStatePath>;

// This is a wrapper to handle the most common use case of:
// 1) making a http GET req (or simply providing the data up front)
// 2) validating the response
// 3) write the data to the state
async function fetchData<D extends object>(
  dataSource: DataSource,
  validate: TransformModelFn<unknown, D>,
  writeStatePath: readonly PropertyKey[],
  { root, ...options }: FetchDataOptions<D>,
): Promise<D> {
  // Backwards compatibility
  root = writeStatePath?.["root"] ?? root;

  if (typeof validate !== "function")
    throw new Error("Validate function required!");

  let validateModel = validate[rootModel] ?? null;

  if (!validateModel) {
    try {
      validate({ [rootModel]: true });

      devError(
        `Validation function did not properly return the root model for "${writeStatePath?.join(
          ".",
        )}".`,
      );
    } catch (e) {
      if (e[rootModel]) {
        validateModel = e[rootModel];
      } else {
        throw e;
      }
    }
  }

  let currentValue: D;

  if (writeStatePath) {
    for (const part of writeStatePath) {
      if (typeof part === "string" && /\$/.test(part)) {
        throw new Error(
          `Illegal character "${PATH_DELIMITER}" in path ${writeStatePath}`,
        );
      }
    }

    if (!root) {
      addRecentlyPersistedPath(writeStatePath);
      writeKeyPath(writeStatePath);
    }

    // Check if we actually need to fetch.
    currentValue = readFromMemory(writeStatePath, {
      root,
      shouldThrow: false,
    }) as D;

    if (shouldReturnCachedValue(currentValue, dataSource, options)) {
      return associateValueToModel(currentValue, validateModel);
    }

    // Second pass: check the DB.
    if (!options.shouldSkipDBRead) {
      currentValue = await populatePathFromDB(
        writeStatePath,
        null,
        root ?? writeState,
      );
    }

    if (shouldReturnCachedValue(currentValue, dataSource, options)) {
      return associateValueToModel(currentValue, validateModel);
    }
  }

  // When a network attempt is about to be attempted, we care about tracking
  // if it's recent or not so we can avoid frequent recurring requests.
  trackURL(dataSource, options.networkBackOffTime);

  let json: unknown;
  let error: unknown;

  try {
    switch (typeof dataSource) {
      case "object": {
        json =
          dataSource &&
          "then" in dataSource &&
          typeof dataSource["then"] === "function"
            ? await dataSource
            : dataSource;
        break;
      }
      case "function": {
        json = await dataSource();
        break;
      }
      case "string": {
        json = await fetchJSON(options.fetch, dataSource, options);

        break;
      }
      default:
        throw new TypeError(
          `dataSource may only be a url, Promise, object, or function.`,
        );
    }
  } catch (e) {
    error = e;
  }

  if (json instanceof Error) error = json;

  // Current value may have changed during this time,
  // let's refresh it from memory.
  currentValue = readFromMemory(writeStatePath, {
    root,
    shouldThrow: false,
  }) as D;

  const v0 = Date.now();
  let validationResult: D;
  if (!error) {
    try {
      validationResult = validate(json);
    } catch (e) {
      error = e;
    }
  }
  devDebug(
    `validated "${
      writeStatePath ? writeStatePath.join(".") : "<empty path>"
    }" in ${Date.now() - v0} ms`,
  );

  if (error) {
    // Should qualify if there is not anything cached.
    if (
      writeStatePath &&
      (!currentValue ||
        (typeof currentValue === "symbol" && currentValue === isLoading))
    ) {
      const [w, lastKey] = digPath(writeStatePath, root ?? writeState);
      w[lastKey] = error;
    }

    // If there was no backoff specified, add minimum backoff for errors.
    if (!options.networkBackOffTime) {
      trackURL(dataSource, mainRefs.ERROR_BACKOFF_TIME);
    }

    throw error;
  }

  if (!validationResult || typeof validationResult !== "object")
    throw new ValidationFailedError(
      "Validate function failed!",
      writeStatePath,
    );

  if (!validationResult[isValidated]) {
    devDebug("Validation failed", validationResult);
    const error = new Error(
      "A validation function needs to be passed here. See `data-model.mjs`. ",
    );
    error["data"] = validationResult;
    throw error;
  }

  if (!validationResult[isVolatile]) {
    validationResult[isPersistent] = options.expiryTime ?? true;
  }

  // Optimization: if the cached value is deep equal to what we got from
  // network request, don't need to write anything and force re-render.
  if (
    writeStatePath &&
    (currentValue?.[isInitial] ||
      validate.skipDeepEqual ||
      !deepEqual(currentValue, validationResult))
  ) {
    const [w, lastKey] = digPath(writeStatePath, root ?? writeState);

    if (options.mergeFn && currentValue) {
      const mergeResult = options.mergeFn(currentValue, validationResult);

      if (!mergeResult[isVolatile]) {
        mergeResult[isPersistent] = options.expiryTime ?? true;
      }

      w[lastKey] = ref(associateValueToModel(mergeResult, validateModel));
    } else {
      w[lastKey] = ref(associateValueToModel(validationResult, validateModel));
    }
  }

  return associateValueToModel(validationResult, validateModel);
}

type GetDataOptions<D> = Partial<SharedFetchOptions<D>> & {
  /**
   * Set to `true` to skip internal checks for bad `getData` patterns.
   *
   * This should be `false` in most cases, unless the response is supplied
   * as the data source.
   */
  skipSafetyCheck?: boolean;
};

/**
 * Fetch data from a data source and write it to the state following validation.
 *
 * If `writeStatePath` is set, TS will attempt to ensure that the `validate` parameter
 * returns a type that matches the type of the state at `writeStatePath`. If you get an
 * overload error or a return type of `any`, check that the `validate` function and the
 * state at `writeStatePath` match.
 *
 * @param dataSource Where the data should be sourced from.
 * @param validate A model validation function--see `data-model.mts`.
 * @param writeStatePath The path to write the data to.
 */
export function getData<
  const P extends readonly PropertyKey[] | undefined,
  S extends DataSource,
  Root extends object,
>(
  dataSource: S,
  validate: TransformModelFn<ResolveDataSource<S>, DigValue<P, Root>>,
  writeStatePath: P,
  options: GetDataOptions<DigValue<P, Root>> & {
    root: Root;
  },
): Promise<DigValue<P, AppState>>;

export function getData<
  const P extends readonly PropertyKey[] | undefined,
  S extends DataSource,
  D,
>(
  dataSource: S,
  validate: TransformModelFn<ResolveDataSource<S>, DigAppStateOr<P, D>>,
  writeStatePath: P,
  options?: GetDataOptions<DigAppState<P>> & {
    root?: never;
  },
  // TODO: this would ideally be `DigAppStateOr<P, D>`, but we need to support non-typed state.
): Promise<
  DigAppStateOr<P, D> extends Record<PrimitiveKey, never>
    ? D
    : DigAppStateOr<P, D>
>;

export function getData<
  O,
  const P extends readonly PropertyKey[],
  D extends object,
>(
  dataSource: DataSource,
  validate: TransformModelFn<O, D>,
  writeStatePath: P | undefined,
  options: GetDataOptions<D> = {},
): Promise<D> {
  if (writeStatePath) {
    if (options.skipSafetyCheck !== true) {
      checkGetData(dataSource, {
        root: writeStatePath?.["root"] ?? options.root,
      });
    }
    // else blockKeyPath(writeStatePath);
  }

  const shouldCheckPromiseMap = typeof dataSource === "string";
  if (shouldCheckPromiseMap) {
    const promise = inFlightURLPromiseMap.get(dataSource);
    if (promise) {
      return promise;
    }
  }

  const result = fetchData(dataSource, validate, writeStatePath, {
    shouldFetchIfPathExists: true,
    networkBackOffTime: mainRefs.DEFAULT_BACKOFF_TIME,

    fetch: fetchRef.fetch,

    ...options,
  });

  if (shouldCheckPromiseMap) {
    inFlightURLPromiseMap.set(dataSource, result);

    // Prevent this map from leaking.
    const destructor = () => {
      inFlightURLPromiseMap.delete(dataSource);
    };
    result.then(destructor, destructor);
  }

  return result;
}

export type PostDataRequest = {
  url: string;
  body: object | BodyInit;
  method?: "POST" | "PUT";
};

type PostDataOptions<D> = Partial<SharedFetchOptions<D>> & {
  skipLoadingPlaceholder?: boolean;

  body?: BodyInit;
};

/**
 * Send a post `request` and write the result to the state following validation.
 *
 * If `writeStatePath` is set, TS will attempt to ensure that the `validate` parameter
 * returns a type that matches the type of the state at `writeStatePath`. If you get an
 * overload error or a return type of `any`, check that the `validate` function and the
 * state at `writeStatePath` match.
 *
 * @param request What `url` to hit with what POST `body`.
 * @param validate A model validation function--see `data-model.mts`.
 * @param writeStatePath The path to write the data to.
 */
export function postData<
  const P extends readonly PropertyKey[],
  Root extends object,
>(
  request: PostDataRequest,
  validate: TransformModelFn<unknown, DigValue<P, Root>>,
  writeStatePath: P,
  options: PostDataOptions<DigValue<P, Root>> & {
    root: Root;
  },
): Promise<DigValue<P, Root>>;

export function postData<const P extends readonly PropertyKey[], D>(
  request: PostDataRequest,
  validate: TransformModelFn<unknown, DigAppStateOr<P, D>>,
  writeStatePath: P,
  options?: PostDataOptions<DigAppStateOr<P, D>> & { root?: never },
): Promise<DigAppStateOr<P, D>>;

export function postData<D extends object>(
  request: PostDataRequest,
  validate: TransformModelFn<unknown, D>,
  writeStatePath: readonly PropertyKey[],
  { root, ...options }: PostDataOptions<D> = {},
): Promise<D> {
  // To return test data set the url param as before
  if (typeof request === "string")
    throw new Error("Request param must be an object of shape { url, body }.");

  if (!options.headers) options.headers = {};

  if (!options.headers.hasOwnProperty("content-type"))
    options.headers["content-type"] = "application/json";

  if (options.skipLoadingPlaceholder !== true && writeStatePath) {
    const [w, lastKey] = digPath(writeStatePath, root ?? writeState);

    w[lastKey] = isLoading;
  }

  const { url, body, method = "POST" } = request;

  return fetchData(url, validate, writeStatePath, {
    root,

    networkBackOffTime: 0,
    httpMethod: method,

    fetch: fetchRef.fetch,

    shouldFetchIfPathExists: true,
    ...options,
    // Specifically check for plain object so that typed arrays work...
    body:
      body?.constructor === Object ? JSON.stringify(body) : (body as BodyInit),
  });
}

globals.__BLITZ_DEV__.getData = getData;
globals.__BLITZ_DEV__.inFlightURLPromiseMap = inFlightURLPromiseMap;

export default getData;
export { dbRef, isPopulatedFromDB, isValidated };
